import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Search, RefreshCw, ChevronDown, ChevronUp, Phone, Mail, 
  MapPin, Clock, Package, DollarSign, CheckCircle, XCircle, 
  AlertCircle, RotateCcw, Trash2, User, Share2, Copy, Check, Truck, ExternalLink
} from 'lucide-react';

// Helper function to capitalize first letter of each word
const toTitleCase = (str) => {
  if (!str) return '';
  return str.toLowerCase().split(' ').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
};

const ManageOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDateFilter, setSelectedDateFilter] = useState('today');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('all');
  const [selectedStatusDropdown, setSelectedStatusDropdown] = useState('all');
  const [selectedOrderType, setSelectedOrderType] = useState('all');
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [storeSettings, setStoreSettings] = useState(null);
  const [ecommerceSettings, setEcommerceSettings] = useState(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  
  // Track Order state
  const [trackOrderModalOpen, setTrackOrderModalOpen] = useState(false);
  const [trackingLink, setTrackingLink] = useState('');
  const [selectedOrderForTracking, setSelectedOrderForTracking] = useState(null);
  
  // Error modal state
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage] = useState(15);
  
  // Search visibility state
  const [showSearchBar, setShowSearchBar] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // Date filter options
  const dateFilters = [
    { value: 'all', label: 'All' },
    { value: 'today', label: 'Today' },
    { value: 'tomorrow', label: 'Tomorrow' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'thisWeek', label: 'This Week' },
    { value: 'lastWeek', label: 'Last Week' }
  ];

  // Status filter options
  const statusFilters = [
    { value: 'all', label: 'All' },
    { value: 'received', label: 'Received' },
    { value: 'inProgress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'refunded', label: 'Refunded' }
  ];

  // Fetch store settings and ecommerce settings
  useEffect(() => {
    fetchStoreSettings();
    fetchEcommerceSettings();
  }, []);

  const fetchStoreSettings = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/store-settings`);
      setStoreSettings(response.data);
    } catch (error) {
      console.error('Error fetching store settings:', error);
    }
  };

  const fetchEcommerceSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${backendUrl}/api/ecommerce-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = response.data;
      
      // Initialize default workflows if they don't exist
      const settingsWithWorkflows = {
        ...data,
        orderNowPickup: {
          ...(data.orderNowPickup || {}),
          workflow: data.orderNowPickup?.workflow || [
            { id: '1', label: 'Received', isFirst: true, isLast: false },
            { id: '2', label: 'Preparing', isFirst: false, isLast: false },
            { id: '3', label: 'Ready for Pickup', isFirst: false, isLast: false },
            { id: '4', label: 'Completed', isFirst: false, isLast: true }
          ]
        },
        orderNowDelivery: {
          ...(data.orderNowDelivery || {}),
          workflow: data.orderNowDelivery?.workflow || [
            { id: '1', label: 'Received', isFirst: true, isLast: false },
            { id: '2', label: 'Preparing', isFirst: false, isLast: false },
            { id: '3', label: 'Out for Delivery', isFirst: false, isLast: false },
            { id: '4', label: 'Delivered', isFirst: false, isLast: true }
          ]
        },
        preOrderPickup: {
          ...(data.preOrderPickup || {}),
          workflow: data.preOrderPickup?.workflow || [
            { id: '1', label: 'Received', isFirst: true, isLast: false },
            { id: '2', label: 'Confirmed', isFirst: false, isLast: false },
            { id: '3', label: 'Preparing', isFirst: false, isLast: false },
            { id: '4', label: 'Ready for Pickup', isFirst: false, isLast: false },
            { id: '5', label: 'Completed', isFirst: false, isLast: true }
          ]
        },
        preOrderDelivery: {
          ...(data.preOrderDelivery || {}),
          workflow: data.preOrderDelivery?.workflow || [
            { id: '1', label: 'Received', isFirst: true, isLast: false },
            { id: '2', label: 'Confirmed', isFirst: false, isLast: false },
            { id: '3', label: 'Preparing', isFirst: false, isLast: false },
            { id: '4', label: 'Out for Delivery', isFirst: false, isLast: false },
            { id: '5', label: 'Delivered', isFirst: false, isLast: true }
          ]
        },
        inStore: {
          ...(data.inStore || {}),
          workflow: data.inStore?.workflow || [
            { id: '1', label: 'Received', isFirst: true, isLast: false },
            { id: '2', label: 'In Progress', isFirst: false, isLast: false },
            { id: '3', label: 'Completed', isFirst: false, isLast: true }
          ]
        },
        shipping: {
          ...(data.shipping || {}),
          workflow: data.shipping?.workflow || [
            { id: '1', label: 'Received', isFirst: true, isLast: false },
            { id: '2', label: 'Processing', isFirst: false, isLast: false },
            { id: '3', label: 'In Transit', isFirst: false, isLast: false },
            { id: '4', label: 'Delivered', isFirst: false, isLast: true }
          ]
        }
      };
      
      console.log('Ecommerce settings with workflows:', settingsWithWorkflows);
      setEcommerceSettings(settingsWithWorkflows);
    } catch (error) {
      console.error('Error fetching ecommerce settings:', error);
    }
  };

  // Get workflow for order type
  const getWorkflowForOrderType = (orderTypeLabel) => {
    console.log('Getting workflow for order type:', orderTypeLabel);
    console.log('ecommerceSettings:', ecommerceSettings);
    
    if (!ecommerceSettings) {
      console.log('No ecommerce settings loaded');
      return null;
    }
    
    // Map order type labels to settings keys
    const orderTypeMap = {
      'Shipping': 'shipping',
      'In Store': 'inStore',
      'Order Now - Pickup': 'orderNowPickup',
      'Order Now - Delivery': 'orderNowDelivery',
      'Pre-Order - Pickup': 'preOrderPickup',
      'Pre-Order - Delivery': 'preOrderDelivery'
    };
    
    const settingsKey = orderTypeMap[orderTypeLabel];
    console.log('Mapped to settings key:', settingsKey);
    console.log('Workflow:', ecommerceSettings[settingsKey]?.workflow);
    
    return settingsKey ? ecommerceSettings[settingsKey]?.workflow : null;
  };

  // Get current step and next step for an order
  const getOrderSteps = (order) => {
    const workflow = getWorkflowForOrderType(order.orderTypeLabel);
    if (!workflow || workflow.length === 0) {
      console.log('No workflow found for order type:', order.orderTypeLabel);
      return { currentStep: null, nextStep: null, currentIndex: -1 };
    }
    
    console.log('Order status:', order.status, 'Workflow:', workflow.map(s => s.label));
    
    // Handle legacy "pending" or "received" status by mapping to first workflow step
    let statusToMatch = order.status.toLowerCase().trim();
    if (statusToMatch === 'pending' || statusToMatch === 'received') {
      // For legacy orders, treat as first step
      console.log('Legacy status detected, using first workflow step');
      return { 
        currentStep: workflow[0], 
        nextStep: workflow.length > 1 ? workflow[1] : null,
        currentIndex: 0
      };
    }
    
    // Find current step by matching status with workflow step labels (case-insensitive)
    const currentIndex = workflow.findIndex(step => 
      step.label.toLowerCase().trim() === statusToMatch
    );
    
    if (currentIndex === -1) {
      // Status doesn't match any workflow step, use first step
      console.log('Status not found in workflow, using first step');
      return { 
        currentStep: workflow[0], 
        nextStep: workflow.length > 1 ? workflow[1] : null,
        currentIndex: 0
      };
    }
    
    const currentStep = workflow[currentIndex];
    const nextStep = currentIndex < workflow.length - 1 ? workflow[currentIndex + 1] : null;
    
    console.log('Current step:', currentStep.label, 'Next step:', nextStep?.label);
    
    return { currentStep, nextStep, currentIndex };
  };

  // Calculate estimated delivery date based on shipping option
  const calculateEstimatedDelivery = (order) => {
    if (order.orderTypeLabel !== 'Shipping') return null;
    
    // Get order creation date
    const parsed = parseBusinessDateTime(order.orderDate);
    if (!parsed) return null;
    
    // Default delivery days if no shipping option
    let deliveryDays = 5;
    
    // Check shipping option for delivery time info
    if (order.shippingOption) {
      const description = order.shippingOption.description || '';
      // Try to extract days from description like "Arrives in 3-5 business days" or "2-3 days"
      const daysMatch = description.match(/(\d+)-(\d+)\s*(business\s*)?days?/i);
      if (daysMatch) {
        // Use the maximum days for safety
        deliveryDays = parseInt(daysMatch[2]);
      } else {
        // Check for single day mention
        const singleDayMatch = description.match(/(\d+)\s*(business\s*)?days?/i);
        if (singleDayMatch) {
          deliveryDays = parseInt(singleDayMatch[1]);
        }
      }
    }
    
    // Calculate delivery date (add days to order date)
    const orderDate = new Date(parsed.year, parsed.month - 1, parsed.day);
    const deliveryDate = new Date(orderDate);
    deliveryDate.setDate(orderDate.getDate() + deliveryDays);
    
    // Format delivery date
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    const deliveryDay = deliveryDate.getDate();
    const deliveryMonth = monthNames[deliveryDate.getMonth()];
    const deliveryDayName = dayNames[deliveryDate.getDay()];
    
    const getOrdinalSuffix = (day) => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    
    return `${deliveryMonth} ${deliveryDay}${getOrdinalSuffix(deliveryDay)} (${deliveryDayName})`;
  };

  // Move order to next workflow step
  const moveToNextStep = async (order) => {
    try {
      const { nextStep } = getOrderSteps(order);
      if (!nextStep) {
        setErrorMessage('No next step available in workflow');
        setErrorModalOpen(true);
        return;
      }
      
      console.log('Moving to next step:', nextStep.label);
      await updateOrderStatus(order.id, nextStep.label);
    } catch (error) {
      console.error('Error moving to next step:', error);
      // Error already handled in updateOrderStatus
    }
  };

  // Parse ISO datetime string without timezone conversion
  const parseBusinessDateTime = (isoString) => {
    if (!isoString) return null;
    
    // Parse ISO string manually to avoid timezone conversion
    // Format: 2024-11-16T12:39:00 or 2024-11-16T12:39:00.123456
    const parts = isoString.split('T');
    if (parts.length !== 2) return null;
    
    const [datePart, timePart] = parts;
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);
    
    return {
      year,
      month, // 1-12
      day,
      hours,
      minutes,
      date: datePart,
      time: timePart
    };
  };

  // Fetch orders
  useEffect(() => {
    fetchOrders();
  }, [selectedDateFilter, selectedStatusDropdown]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      
      if (selectedStatusDropdown !== 'all') {
        params.status = selectedStatusDropdown;
      }

      const response = await axios.get(`${backendUrl}/api/orders`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      console.log('Fetched orders:', response.data.length);
      console.log('Orders data:', response.data);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter orders by date
  const filterOrdersByDate = (orders) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    console.log('Filtering orders by date. Selected filter:', selectedDateFilter);
    console.log('Today:', today);
    console.log('Total orders before filter:', orders.length);
    
    const filtered = orders.filter(order => {
      const parsed = parseBusinessDateTime(order.orderDate);
      if (!parsed) return true; // If can't parse, show it
      
      // Create date without time for comparison (in business timezone)
      const orderDay = new Date(parsed.year, parsed.month - 1, parsed.day);
      
      console.log('Order:', order.order_number, 'Date:', order.orderDate, 'Day:', orderDay);
      
      switch (selectedDateFilter) {
        case 'all':
          return true; // Show all orders
        case 'today':
          const isToday = orderDay.getTime() === today.getTime();
          console.log('Is today?', isToday, 'Order day:', orderDay.getTime(), 'Today:', today.getTime());
          return isToday;
        case 'tomorrow':
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);
          return orderDay.getTime() === tomorrow.getTime();
        case 'yesterday':
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          return orderDay.getTime() === yesterday.getTime();
        case 'thisWeek':
          const startOfWeek = new Date(today);
          startOfWeek.setDate(today.getDate() - today.getDay());
          const endOfWeek = new Date(startOfWeek);
          endOfWeek.setDate(startOfWeek.getDate() + 6);
          return orderDay >= startOfWeek && orderDay <= endOfWeek;
        case 'lastWeek':
          const lastWeekStart = new Date(today);
          lastWeekStart.setDate(today.getDate() - today.getDay() - 7);
          const lastWeekEnd = new Date(lastWeekStart);
          lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
          return orderDay >= lastWeekStart && orderDay <= lastWeekEnd;
        default:
          return true;
      }
    });
    
    console.log('Orders after date filter:', filtered.length);
    return filtered;
  };

  // Filter orders by order type
  const filterOrdersByType = (orders) => {
    if (selectedOrderType === 'all') return orders;
    return orders.filter(order => order.orderTypeLabel === selectedOrderType);
  };

  // Filter orders by status
  const filterOrdersByStatus = (orders) => {
    if (selectedStatusFilter === 'all') return orders;
    return orders.filter(order => {
      const status = order.status.toLowerCase();
      return status === selectedStatusFilter.toLowerCase() || 
             (selectedStatusFilter === 'inProgress' && status === 'in progress') ||
             (selectedStatusFilter === 'received' && (status === 'pending' || status === 'received'));
    });
  };

  // Search orders
  const searchOrders = (orders) => {
    if (!searchTerm.trim()) return orders;
    
    const search = searchTerm.toLowerCase();
    return orders.filter(order => 
      order.order_number.toLowerCase().includes(search) ||
      order.customer.name.toLowerCase().includes(search) ||
      order.customer.email.toLowerCase().includes(search) ||
      order.customer.phone.toLowerCase().includes(search)
    );
  };

  // Get filtered orders
  const filteredOrders = searchOrders(filterOrdersByStatus(filterOrdersByType(filterOrdersByDate(orders))));

  // Pagination logic
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedDateFilter, selectedStatusFilter, searchTerm, selectedOrderType]);

  // Pagination controls
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToPage = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Get order counts by status (filtered by order type and date)
  const getOrderCounts = () => {
    const typeFiltered = filterOrdersByType(orders);
    const dateFiltered = filterOrdersByDate(typeFiltered);
    return {
      all: dateFiltered.length,
      received: dateFiltered.filter(o => ['pending', 'received'].includes(o.status.toLowerCase())).length,
      inProgress: dateFiltered.filter(o => o.status.toLowerCase() === 'in progress' || o.status.toLowerCase() === 'inprogress').length,
      completed: dateFiltered.filter(o => o.status.toLowerCase() === 'completed').length,
      cancelled: dateFiltered.filter(o => o.status.toLowerCase() === 'cancelled').length,
      refunded: dateFiltered.filter(o => o.status.toLowerCase() === 'refunded').length
    };
  };

  const counts = getOrderCounts();

  // Get counts for each date filter (filtered by order type)
  const getDateFilterCounts = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // First filter by order type
    const typeFilteredOrders = filterOrdersByType(orders);
    
    const countsByFilter = {
      all: typeFilteredOrders.length,
      today: 0,
      yesterday: 0,
      thisWeek: 0
    };
    
    typeFilteredOrders.forEach(order => {
      const parsed = parseBusinessDateTime(order.orderDate);
      if (!parsed) return;
      
      // Create date without time for comparison (in business timezone)
      const orderDay = new Date(parsed.year, parsed.month - 1, parsed.day);
      const diffTime = today.getTime() - orderDay.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      // Today
      if (diffDays === 0) {
        countsByFilter.today++;
      }
      
      // Yesterday
      if (diffDays === 1) {
        countsByFilter.yesterday++;
      }
      
      // This week (last 7 days)
      if (diffDays >= 0 && diffDays <= 6) {
        countsByFilter.thisWeek++;
      }
    });
    
    return countsByFilter;
  };
  
  const dateFilterCounts = getDateFilterCounts();

  // Update order status
  const updateOrderStatus = async (orderId, newStatus) => {
    setActionLoading(orderId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${backendUrl}/api/orders/${orderId}/status`,
        { status: newStatus },
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { status: newStatus }
        }
      );
      
      console.log('Status update response:', response);
      
      // Refresh orders
      await fetchOrders();
    } catch (error) {
      console.error('Error updating order status:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to update order status';
      setErrorMessage(errorMsg);
      setErrorModalOpen(true);
      throw error; // Re-throw to be caught by moveToNextStep
    } finally {
      setActionLoading(null);
    }
  };

  // Delete order
  const deleteOrder = async (orderId, orderNumber) => {
    const confirmed = window.confirm(
      `Are you sure you want to permanently delete order ${orderNumber}?\n\nThis action cannot be undone!`
    );
    
    if (!confirmed) return;
    
    setActionLoading(orderId);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${backendUrl}/api/orders/${orderId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert(`Order ${orderNumber} deleted successfully`);
      
      // Refresh orders
      await fetchOrders();
    } catch (error) {
      console.error('Error deleting order:', error);
      alert('Failed to delete order. You may not have permission.');
    } finally {
      setActionLoading(null);
    }
  };

  // Generate shareable link for order
  const shareOrder = async (orderId) => {
    setActionLoading(orderId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${backendUrl}/api/orders/${orderId}/share`, 
        {},
        { headers: { 'Authorization': `Bearer ${token}` }}
      );
      
      const shareToken = response.data.shareToken;
      const link = `${window.location.origin}/shared-order/${shareToken}`;
      
      setShareLink(link);
      setShareModalOpen(true);
      setLinkCopied(false);
    } catch (error) {
      console.error('Error generating share link:', error);
      alert('Failed to generate share link. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  // Copy link to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  // Open Track Order modal
  const openTrackOrderModal = (order) => {
    setSelectedOrderForTracking(order);
    setTrackingLink(order.tracking_link || '');
    setTrackOrderModalOpen(true);
  };

  // Save tracking link
  const saveTrackingLink = async () => {
    if (!selectedOrderForTracking) return;
    
    setActionLoading(selectedOrderForTracking.id);
    try {
      const token = localStorage.getItem('token');
      console.log('Saving tracking link for order:', selectedOrderForTracking.id);
      console.log('Tracking link:', trackingLink);
      
      const response = await axios.patch(
        `${backendUrl}/api/orders/${selectedOrderForTracking.id}`,
        { tracking_link: trackingLink },
        { headers: { 'Authorization': `Bearer ${token}` }}
      );
      
      console.log('Tracking link save response:', response);
      
      // Update local state
      setOrders(orders.map(order => 
        order.id === selectedOrderForTracking.id 
          ? { ...order, tracking_link: trackingLink }
          : order
      ));
      
      setTrackOrderModalOpen(false);
      setSelectedOrderForTracking(null);
      setTrackingLink('');
    } catch (error) {
      console.error('Error saving tracking link:', error);
      console.error('Error response:', error.response);
      
      const errorMsg = error.response?.data?.detail || 
                      error.response?.data?.message || 
                      error.message || 
                      'Failed to save tracking link. Please check the URL and try again.';
      
      setErrorMessage(errorMsg);
      setErrorModalOpen(true);
    } finally {
      setActionLoading(null);
    }
  };

  // Format date/time without timezone conversion - treats stored time as business time
  const formatDateTime = (dateStr) => {
    const parsed = parseBusinessDateTime(dateStr);
    if (!parsed) {
      // Fallback for old format
      const date = new Date(dateStr);
      return {
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
      };
    }
    
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const formattedDate = `${monthNames[parsed.month - 1]} ${parsed.day}, ${parsed.year}`;
    
    // Format time in 12-hour format
    const hours12 = parsed.hours % 12 || 12;
    const ampm = parsed.hours >= 12 ? 'PM' : 'AM';
    const formattedMinutes = parsed.minutes < 10 ? `0${parsed.minutes}` : parsed.minutes;
    const formattedTime = `${hours12}:${formattedMinutes} ${ampm}`;
    
    return {
      date: formattedDate,
      time: formattedTime
    };
  };

  // Format date/time for accordion header - e.g., "Nov 5th, 5:30 PM EST"
  const formatOrderHeaderDateTime = (dateStr) => {
    if (!dateStr) return '';
    
    const parsed = parseBusinessDateTime(dateStr);
    if (!parsed) {
      // Fallback for old format
      const date = new Date(dateStr);
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const day = date.getDate();
      const month = monthNames[date.getMonth()];
      const getOrdinalSuffix = (day) => {
        if (day > 3 && day < 21) return 'th';
        switch (day % 10) {
          case 1: return 'st';
          case 2: return 'nd';
          case 3: return 'rd';
          default: return 'th';
        }
      };
      const ordinalDay = `${day}${getOrdinalSuffix(day)}`;
      let hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
      return `${month} ${ordinalDay}, ${hours}:${formattedMinutes} ${ampm}`;
    }
    
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[parsed.month - 1];
    
    // Add ordinal suffix (st, nd, rd, th)
    const getOrdinalSuffix = (day) => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    
    const ordinalDay = `${parsed.day}${getOrdinalSuffix(parsed.day)}`;
    
    // Format time in 12-hour format with AM/PM
    const hours12 = parsed.hours % 12 || 12;
    const ampm = parsed.hours >= 12 ? 'PM' : 'AM';
    const formattedMinutes = parsed.minutes < 10 ? `0${parsed.minutes}` : parsed.minutes;
    
    return `${month} ${ordinalDay}, ${hours12}:${formattedMinutes} ${ampm} EST`;
  };

  // Format date with day name - e.g., "Nov 16th (Mon) at 12:15 PM EST" or "Nov 16th (Mon) between 2 PM - 5 PM EST"
  const formatOrderDateTime = (dateStr, timeStr) => {
    if (!dateStr) return timeStr || '';
    
    const parsed = parseBusinessDateTime(dateStr);
    if (!parsed) {
      // Fallback for old format
      const date = new Date(dateStr);
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const day = date.getDate();
      const month = monthNames[date.getMonth()];
      const dayName = dayNames[date.getDay()];
      const getOrdinalSuffix = (day) => {
        if (day > 3 && day < 21) return 'th';
        switch (day % 10) {
          case 1: return 'st';
          case 2: return 'nd';
          case 3: return 'rd';
          default: return 'th';
        }
      };
      const ordinalDay = `${day}${getOrdinalSuffix(day)}`;
      if (timeStr) {
        return `${month} ${ordinalDay} (${dayName}) ${timeStr}`;
      }
      let hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
      return `${month} ${ordinalDay} (${dayName}) at ${hours}:${formattedMinutes} ${ampm}`;
    }
    
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    // Calculate day of week from date (using local Date just for day of week calculation)
    const jsDate = new Date(parsed.year, parsed.month - 1, parsed.day);
    const dayName = dayNames[jsDate.getDay()];
    const month = monthNames[parsed.month - 1];
    
    // Add ordinal suffix (st, nd, rd, th)
    const getOrdinalSuffix = (day) => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    
    const ordinalDay = `${parsed.day}${getOrdinalSuffix(parsed.day)}`;
    
    // Helper function to format a single time
    const formatTime = (timeString) => {
      const parts = timeString.split(':');
      if (parts.length >= 2) {
        let hours = parseInt(parts[0]);
        const minutes = parseInt(parts[1]);
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12;
        
        // Only show minutes if they're not :00
        if (minutes === 0) {
          return `${hours} ${ampm}`;
        }
        return `${hours}:${minutes < 10 ? '0' + minutes : minutes} ${ampm}`;
      }
      return timeString;
    };
    
    // Format time if provided
    let formattedTime = '';
    if (timeStr) {
      // Check if it's a time range (contains dash or hyphen)
      if (timeStr.includes('-')) {
        const [startTime, endTime] = timeStr.split('-').map(t => t.trim());
        const formattedStart = formatTime(startTime);
        const formattedEnd = formatTime(endTime);
        formattedTime = ` between ${formattedStart} - ${formattedEnd} EST`;
      } else {
        // Single time
        formattedTime = ` at ${formatTime(timeStr)} EST`;
      }
    }
    
    return `${month} ${ordinalDay} (${dayName})${formattedTime}`;
  };

  // Get pickup location display (now stores full info in order)
  const getPickupLocationDisplay = (pickupLocation) => {
    if (!pickupLocation || pickupLocation === '') {
      return null;
    }
    
    // Check if it's an old order with just an index
    const index = parseInt(pickupLocation);
    if (!isNaN(index) && pickupLocation.length <= 2) {
      // This is an old order with index - try to look it up if store settings available
      if (storeSettings) {
        const orderNowLocations = storeSettings.orderNowPickup?.pickupLocations || [];
        const preOrderLocations = storeSettings.preOrderPickup?.pickupLocations || [];
        const allLocations = [...orderNowLocations, ...preOrderLocations];
        
        if (allLocations[index]) {
          const location = allLocations[index];
          return `${location.name} - ${location.address}`;
        }
      }
      return `Location #${pickupLocation}`;
    }
    
    // New orders store full location info, just return it
    return pickupLocation;
  };

  // Get status color
  const getStatusColor = (status) => {
    // All statuses use the same cyan color
    return 'bg-[rgb(41,173,211)]';
  };

  // Get action buttons based on status
  const getActionButtons = (order) => {
    const status = order.status.toLowerCase();
    
    if (status === 'received' || status === 'pending') {
      return (
        <>
          <button
            onClick={() => updateOrderStatus(order.id, 'in progress')}
            disabled={actionLoading === order.id}
            className="px-3 py-1.5 bg-theme-blue text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
          >
            Mark In Progress
          </button>
          <button
            onClick={() => updateOrderStatus(order.id, 'cancelled')}
            disabled={actionLoading === order.id}
            className="px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => updateOrderStatus(order.id, 'refunded')}
            disabled={actionLoading === order.id}
            className="px-3 py-1.5 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 disabled:opacity-50"
          >
            Refund
          </button>
        </>
      );
    } else if (status === 'in progress' || status === 'inprogress') {
      return (
        <>
          <button
            onClick={() => updateOrderStatus(order.id, 'completed')}
            disabled={actionLoading === order.id}
            className="px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50"
          >
            Mark Complete
          </button>
          <button
            onClick={() => updateOrderStatus(order.id, 'cancelled')}
            disabled={actionLoading === order.id}
            className="px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50"
          >
            Cancel
          </button>
        </>
      );
    }
    
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        
        {/* Header Card */}
        <div className="bg-white rounded-xl shadow-sm mb-6">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h1 className="text-lg font-semibold text-gray-900">Manage Orders</h1>
            <div className="flex items-center gap-3">
              {/* Order Type Filter - Top Level */}
              <select
                value={selectedOrderType}
                onChange={(e) => setSelectedOrderType(e.target.value)}
                className="text-white text-sm px-3 py-1.5 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors appearance-none pr-8"
                style={{ 
                  backgroundColor: 'var(--theme-primary)',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                  backgroundPosition: 'right 6px center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '14px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--theme-primary-hover)'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
              >
                <option value="all" style={{ backgroundColor: 'white', color: 'black' }}>All Order Types</option>
                {ecommerceSettings && (
                  <>
                    {ecommerceSettings.orderNowPickup?.enabled && (
                      <option value="Order Now - Pickup" style={{ backgroundColor: 'white', color: 'black' }}>Order Now - Pickup</option>
                    )}
                    {ecommerceSettings.preOrderPickup?.enabled && (
                      <option value="Pre-Order - Pickup" style={{ backgroundColor: 'white', color: 'black' }}>Pre-Order - Pickup</option>
                    )}
                    {ecommerceSettings.orderNowDelivery?.enabled && (
                      <option value="Order Now - Delivery" style={{ backgroundColor: 'white', color: 'black' }}>Order Now - Delivery</option>
                    )}
                    {ecommerceSettings.preOrderDelivery?.enabled && (
                      <option value="Pre-Order - Delivery" style={{ backgroundColor: 'white', color: 'black' }}>Pre-Order - Delivery</option>
                    )}
                    {ecommerceSettings.shipping?.enabled && (
                      <option value="Shipping" style={{ backgroundColor: 'white', color: 'black' }}>Shipping</option>
                    )}
                    {ecommerceSettings.inStore?.enabled && (
                      <option value="In Store" style={{ backgroundColor: 'white', color: 'black' }}>In Store</option>
                    )}
                  </>
                )}
              </select>
              <button
                onClick={() => setShowSearchBar(!showSearchBar)}
                className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 hover:text-gray-900 rounded-md transition-colors"
              >
                <Search className="h-4 w-4" />
                <span className="text-sm font-medium">Search</span>
              </button>
            </div>
          </div>
        </div>

        {/* Search and Filter Bar - Conditionally rendered */}
        {showSearchBar && (
          <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200 mb-6">
            <div className="flex flex-wrap gap-2">
              <div className="flex-1 min-w-[200px] relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchTerm(value);
                    // Auto-switch to "All" tab when user starts searching
                    if (value.trim() !== '' && selectedDateFilter !== 'all') {
                      setSelectedDateFilter('all');
                    }
                  }}
                  placeholder="Search by order number, name, email, phone..."
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              
              <select
                value={selectedStatusDropdown}
                onChange={(e) => setSelectedStatusDropdown(e.target.value)}
                className="text-white text-sm px-3 py-1.5 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors appearance-none pr-8"
                style={{ 
                  backgroundColor: '#7c3aed',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                  backgroundPosition: 'right 6px center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '14px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#6d28d9'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#7c3aed'}
              >
                <option value="all" style={{ backgroundColor: 'white', color: 'black' }}>All Status</option>
                <option value="received" style={{ backgroundColor: 'white', color: 'black' }}>Received</option>
                <option value="in progress" style={{ backgroundColor: 'white', color: 'black' }}>In Progress</option>
                <option value="completed" style={{ backgroundColor: 'white', color: 'black' }}>Completed</option>
                <option value="cancelled" style={{ backgroundColor: 'white', color: 'black' }}>Cancelled</option>
                <option value="refunded" style={{ backgroundColor: 'white', color: 'black' }}>Refunded</option>
              </select>
              <button
                onClick={fetchOrders}
                className="text-white text-sm px-3 py-1.5 rounded-md transition-colors flex items-center justify-center font-medium"
                style={{ backgroundColor: '#f59e0b' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#d97706'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#f59e0b'}
              >
                <RefreshCw className="h-4 w-4 mr-1.5" />
                Refresh
              </button>
            </div>
          </div>
        )}

        {/* Date Filter Tabs with Total Amount */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-x-auto">
          <div className="flex border-b border-gray-200 items-center">
            <div className="flex flex-1">
              {dateFilters
                .filter(filter => filter.value !== 'all') // Hide "All" tab from UI
                .map(filter => {
                  const count = dateFilterCounts[filter.value] || 0;
                  return (
                    <button
                      key={filter.value}
                      onClick={() => {
                        setSelectedDateFilter(filter.value);
                        // Clear search when switching tabs
                        if (searchTerm !== '') {
                          setSearchTerm('');
                        }
                      }}
                      className={`px-6 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                        selectedDateFilter === filter.value
                          ? 'border-b-2'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                      style={selectedDateFilter === filter.value ? {
                        color: 'var(--theme-primary)',
                        borderColor: 'var(--theme-primary)'
                      } : {}}
                    >
                      {filter.label} {count > 0 && <span className="ml-1" style={{ color: 'var(--theme-primary)' }}>({count})</span>}
                      {count === 0 && <span className="ml-1 text-gray-400">({count})</span>}
                    </button>
                  );
                })}
            </div>
            <div className="px-6 py-2 text-lg font-bold text-gray-900" style={{ lineHeight: '1.45rem' }}>
              ${filteredOrders.reduce((sum, order) => sum + order.total, 0).toFixed(2)}
            </div>
          </div>
        </div>

        {/* Status Filter Tabs - Sticky below header */}
        <div className="sticky top-[103px] z-40 bg-white rounded-lg shadow-md border border-gray-200 mb-6 overflow-x-auto">
          <div className="flex border-b border-gray-200">
            {statusFilters.map(filter => (
              <button
                key={filter.value}
                onClick={() => setSelectedStatusFilter(filter.value)}
                className={`px-6 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                  selectedStatusFilter === filter.value
                    ? 'border-b-2'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
                style={selectedStatusFilter === filter.value ? {
                  color: 'var(--theme-primary)',
                  borderColor: 'var(--theme-primary)'
                } : {}}
              >
                {filter.label} {(filter.value === 'all' ? counts.all : counts[filter.value] || 0) > 0 ? (
                  <span style={{ color: 'var(--theme-primary)' }}>({filter.value === 'all' ? counts.all : counts[filter.value] || 0})</span>
                ) : (
                  <span className="text-gray-400">({filter.value === 'all' ? counts.all : counts[filter.value] || 0})</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Orders List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-blue mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading orders...</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="bg-white p-12 rounded-lg shadow-sm border border-gray-200 text-center">
            <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">No orders found</p>
            <p className="text-gray-500 text-sm mt-2">Try adjusting your filters or search criteria</p>
          </div>
        ) : (
          <div className="space-y-4">
            {currentOrders.map(order => {
              const { date, time } = formatDateTime(order.orderDate);
              const isExpanded = expandedOrderId === order.id;
              
              return (
                <div key={order.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  {/* Order Header - Clickable */}
                  <div
                    onClick={() => setExpandedOrderId(isExpanded ? null : order.id)}
                    className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-sm font-semibold text-gray-900">{toTitleCase(order.customer.name)}</h3>
                        </div>
                        {order.orderTypeLabel === 'Shipping' && order.shippingOption ? (
                          <div className="flex items-center gap-1 text-xs text-gray-600">
                            <span>
                              {order.shippingOption.name}
                              {calculateEstimatedDelivery(order) && (
                                <span className="ml-2">• Est. Delivery: {calculateEstimatedDelivery(order)}</span>
                              )}
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-xs text-gray-600">
                            <span>
                              <span className="font-medium">
                                {order.orderTypeLabel || (
                                  order.orderType === 'orderNowPickup' ? 'Order Now - Pickup' :
                                  order.orderType === 'preOrderPickup' ? 'Pre-Order - Pickup' :
                                  order.orderType === 'orderNowDelivery' ? 'Order Now - Delivery' :
                                  order.orderType === 'preOrderDelivery' ? 'Pre-Order - Delivery' :
                                  order.orderType
                                )}
                                {': '}
                              </span>
                              {order.pickupDate || order.deliveryDate ? (
                                formatOrderDateTime(
                                  order.pickupDate || order.deliveryDate,
                                  order.pickupTime || order.deliveryTime
                                )
                              ) : (order.pickupTime || order.deliveryTime) ? (
                                // If only time is available (Order Now), show today's date with the time
                                `${formatOrderHeaderDateTime(order.orderDate).split(',')[0]}, ${order.pickupTime || order.deliveryTime} EST`
                              ) : (
                                formatOrderHeaderDateTime(order.orderDate)
                              )}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        {/* Current Workflow Step Badge */}
                        {(() => {
                          const { currentStep, nextStep } = getOrderSteps(order);
                          console.log('Rendering badge - currentStep:', currentStep, 'nextStep:', nextStep);
                          return currentStep ? (
                            <div className="flex items-center gap-2">
                              <span className={`px-3 py-1 rounded text-xs font-medium text-white ${getStatusColor(order.status)}`}>
                                {currentStep.label}
                              </span>
                              {/* Next Step Button */}
                              {nextStep && !isExpanded && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    moveToNextStep(order);
                                  }}
                                  disabled={actionLoading === order.id}
                                  className="px-2 py-1 bg-[rgb(41,173,211)] text-white text-xs font-medium rounded hover:bg-[rgb(31,153,191)] disabled:opacity-50 flex items-center gap-1"
                                  title={`Move to: ${nextStep.label}`}
                                >
                                  → {nextStep.label}
                                </button>
                              )}
                            </div>
                          ) : (
                            <span className={`px-3 py-1 rounded text-xs font-medium text-white ${getStatusColor(order.status)}`}>
                              {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                            </span>
                          );
                        })()}
                        <div className="text-right">
                          <p className="text-base font-bold text-gray-900">${order.total.toFixed(2)}</p>
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="h-5 w-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Order Details - Expandable */}
                  {isExpanded && (
                    <div className="border-t border-gray-200 p-4 bg-gray-50">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        {/* Customer Details */}
                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                          <h4 className="font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-100">Customer Details</h4>
                          <div className="space-y-2.5">
                            <div className="flex items-center gap-2 text-sm">
                              <User className="h-4 w-4 text-theme-blue" />
                              <span className="text-gray-700 font-medium">{toTitleCase(order.customer.name)}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <Phone className="h-4 w-4 text-theme-blue" />
                              <span className="text-gray-700">{order.customer.phone}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <Mail className="h-4 w-4 text-theme-blue" />
                              <span className="text-gray-700">{order.customer.email}</span>
                            </div>
                          </div>
                        </div>

                        {/* Order Details */}
                        <div className="bg-white p-4 rounded-lg border border-gray-200 md:col-span-2">
                          <h4 className="font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-100">Order Details</h4>
                          <div className="space-y-2.5">
                            {/* Order Number */}
                            <div className="flex items-center gap-2 text-sm">
                              <Package className="h-4 w-4 text-gray-500" />
                              <span className="text-gray-700">
                                <span className="font-medium">Order Number:</span> {order.order_number}
                              </span>
                            </div>

                            {/* Order Type */}
                            <div className="flex items-center gap-2 text-sm">
                              <Package className="h-4 w-4 text-green-500" />
                              <span className="text-gray-700">
                                <span className="font-medium">Order Type:</span> {order.orderTypeLabel || (
                                  order.orderType === 'orderNowPickup' ? 'Order Now - Pickup' :
                                  order.orderType === 'preOrderPickup' ? 'Pre-Order - Pickup' :
                                  order.orderType === 'orderNowDelivery' ? 'Order Now - Delivery' :
                                  order.orderType === 'preOrderDelivery' ? 'Pre-Order - Delivery' :
                                  order.orderType
                                )}
                              </span>
                            </div>

                            {/* Order Creation Date */}
                            <div className="flex items-center gap-2 text-sm">
                              <Clock className="h-4 w-4 text-theme-blue" />
                              <span className="text-gray-700">
                                <span className="font-medium">Order Date:</span> {formatOrderHeaderDateTime(order.orderDate)}
                              </span>
                            </div>

                            {/* Pickup/Delivery Time */}
                            {(order.pickupDate || order.deliveryDate || order.pickupTime || order.deliveryTime) && (
                              <div className="flex items-center gap-2 text-sm">
                                <Clock className="h-4 w-4 text-purple-500" />
                                <span className="text-gray-700">
                                  <span className="font-medium">
                                    {order.orderType && order.orderType.includes('Pickup') ? 'Pickup Time:' : 
                                     order.orderType && order.orderType.includes('Delivery') ? 'Delivery Time:' : 
                                     'Scheduled Time:'}
                                  </span> {
                                    order.pickupDate || order.deliveryDate ? (
                                      formatOrderDateTime(
                                        order.pickupDate || order.deliveryDate,
                                        order.pickupTime || order.deliveryTime
                                      )
                                    ) : (order.pickupTime || order.deliveryTime) ? (
                                      // Show date from order creation + selected time + EST
                                      (() => {
                                        const orderDateFormatted = formatOrderHeaderDateTime(order.orderDate);
                                        const dateOnly = orderDateFormatted.split(',')[0]; // Gets "Nov 17th"
                                        const timeStr = order.pickupTime || order.deliveryTime;
                                        // Format time to 12-hour with AM/PM
                                        const [hours, minutes] = timeStr.split(':');
                                        const hour = parseInt(hours);
                                        const ampm = hour >= 12 ? 'PM' : 'AM';
                                        const hour12 = hour % 12 || 12;
                                        return `${dateOnly}, ${hour12}:${minutes} ${ampm} EST`;
                                      })()
                                    ) : 'N/A'
                                  }
                                </span>
                              </div>
                            )}

                            {/* Pickup Location */}
                            {order.pickupLocation && (
                              <div className="flex items-start gap-2 text-sm">
                                <MapPin className="h-4 w-4 text-orange-500 mt-0.5" />
                                <span className="text-gray-700">
                                  <span className="font-medium">Pickup Location:</span> {getPickupLocationDisplay(order.pickupLocation)}
                                </span>
                              </div>
                            )}

                            {/* Delivery Address */}
                            {order.deliveryAddress && !order.pickupLocation && (
                              <div className="flex items-start gap-2 text-sm">
                                <MapPin className="h-4 w-4 text-orange-500 mt-0.5" />
                                <span className="text-gray-700">
                                  <span className="font-medium">Delivery Address:</span> {order.deliveryAddress}
                                </span>
                              </div>
                            )}

                            {/* Shipping Method */}
                            {order.orderTypeLabel === 'Shipping' && order.shippingOption && (
                              <div className="flex items-center gap-2 text-sm">
                                <Truck className="h-4 w-4 text-purple-500" />
                                <span className="text-gray-700">
                                  <span className="font-medium">Shipping Method:</span> {order.shippingOption.name}
                                  {order.shippingOption.description && (
                                    <span className="ml-2 text-xs text-gray-500">({order.shippingOption.description})</span>
                                  )}
                                </span>
                              </div>
                            )}

                            {/* Estimated Delivery Date for Shipping Orders */}
                            {order.orderTypeLabel === 'Shipping' && calculateEstimatedDelivery(order) && (
                              <div className="flex items-center gap-2 text-sm">
                                <Clock className="h-4 w-4 text-green-500" />
                                <span className="text-gray-700">
                                  <span className="font-medium">Est. Delivery Date:</span> {calculateEstimatedDelivery(order)}
                                </span>
                              </div>
                            )}

                            {/* Tracking Link */}
                            {order.tracking_link && (
                              <div className="flex items-center gap-2 text-sm">
                                <Truck className="h-4 w-4 text-[rgb(41,173,211)]" />
                                <span className="text-gray-700">
                                  <span className="font-medium">Tracking URL:</span>{' '}
                                  <a
                                    href={order.tracking_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[rgb(41,173,211)] hover:text-[rgb(31,153,191)] underline inline-flex items-center gap-1"
                                  >
                                    Track Shipment
                                    <ExternalLink className="h-3 w-3" />
                                  </a>
                                </span>
                              </div>
                            )}

                            {/* Delivery Instructions */}
                            {order.deliveryInstructions && (
                              <div className="flex items-start gap-2 text-sm">
                                <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5" />
                                <span className="text-gray-700">
                                  <span className="font-medium">Delivery Instructions:</span> <span className="text-xs italic">"{order.deliveryInstructions}"</span>
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Order Items - Full Width */}
                      <div className="mb-6">
                        <h4 className="font-semibold text-gray-900 mb-3">Order Items</h4>
                        <div className="space-y-3">
                            {order.items.map((item, idx) => (
                              <div key={idx} className="bg-white p-3 rounded border border-gray-200">
                                <div className="flex justify-between text-sm mb-1">
                                  <span className="text-gray-900 font-medium">
                                    {item.quantity}x {item.name || item.product_name || 'Product'}
                                  </span>
                                  <span className="text-gray-900 font-semibold">
                                    ${(item.price * item.quantity).toFixed(2)}
                                  </span>
                                </div>
                                
                                {/* Show variant if available */}
                                {item.variant && (
                                  <div className="text-xs text-theme-blue ml-4 mt-1">
                                    • {item.variation_type && `${item.variation_type.charAt(0).toUpperCase() + item.variation_type.slice(1)}: `}{item.variant.name}
                                    {item.variant.price && ` ($${parseFloat(item.variant.price).toFixed(2)})`}
                                  </div>
                                )}
                                
                                {/* Show selected options if available */}
                                {item.selectedOptions && item.options && (
                                  <div className="text-xs text-gray-600 ml-4 mt-1 space-y-0.5">
                                    {item.options.map(option => {
                                      const selectedValue = item.selectedOptions[option.option_id];
                                      if (!selectedValue) return null;
                                      
                                      if (option.option_type === 'single') {
                                        return (
                                          <div key={option.option_id}>
                                            • {option.option_name}
                                            {option.price && ` (+$${parseFloat(option.price).toFixed(2)})`}
                                          </div>
                                        );
                                      } else if (option.option_type === 'dropdown') {
                                        const choice = option.choices?.find(c => c.choice_id === selectedValue);
                                        if (!choice) return null;
                                        return (
                                          <div key={option.option_id}>
                                            • {option.option_name}: {choice.choice_name}
                                            {choice.price && ` (+$${parseFloat(choice.price).toFixed(2)})`}
                                          </div>
                                        );
                                      }
                                      return null;
                                    })}
                                  </div>
                                )}
                                
                                {item.price && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    Unit Price: ${item.price.toFixed(2)}
                                  </div>
                                )}
                                
                                {/* Debug: Show raw item data if name is missing */}
                                {!item.name && !item.product_name && (
                                  <div className="text-xs text-red-500 mt-1">
                                    Debug: {JSON.stringify(item)}
                                  </div>
                                )}
                              </div>
                            ))}
                        </div>
                      </div>

                      {/* Special Instructions */}
                      {order.specialInstructions && (
                        <div className="mb-6">
                          <h4 className="font-semibold text-gray-900 mb-2">Special Instructions</h4>
                          <p className="text-sm text-gray-700 bg-white p-3 rounded border border-gray-200">
                            {order.specialInstructions}
                          </p>
                        </div>
                      )}

                      {/* Order Summary */}
                      <div className="bg-white p-4 rounded border border-gray-200 mb-6">
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Subtotal:</span>
                            <span className="text-gray-900">${order.subtotal.toFixed(2)}</span>
                          </div>
                          {order.shippingFee > 0 && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Shipping:</span>
                              <span className="text-gray-900">${order.shippingFee.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-gray-600">Tax:</span>
                            <span className="text-gray-900">${order.tax.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between pt-2 border-t border-gray-200">
                            <span className="font-semibold text-gray-900">Total:</span>
                            <span className="font-bold text-gray-900">${order.total.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-xs text-gray-500">
                            <span>Payment Method:</span>
                            <span className="uppercase">{order.paymentMethod}</span>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-wrap gap-2 items-center justify-end">
                        <div className="flex flex-wrap gap-2">
                          {/* Share Order Button */}
                          <button
                            onClick={() => shareOrder(order.id)}
                            disabled={actionLoading === order.id}
                            className="px-3 py-1.5 bg-theme-blue text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                            title="Generate shareable link for this order"
                          >
                            <Share2 className="h-4 w-4" />
                            Share Order
                          </button>
                          
                          {/* Track Order Button (Only for Shipping orders) */}
                          {(order.orderType === 'shipping' || order.orderTypeLabel === 'Shipping') && (
                            <button
                              onClick={() => openTrackOrderModal(order)}
                              disabled={actionLoading === order.id}
                              className="px-3 py-1.5 bg-[rgb(41,173,211)] text-white text-sm rounded hover:bg-[rgb(31,153,191)] disabled:opacity-50 flex items-center gap-1"
                              title={order.tracking_link ? "Edit tracking link" : "Add tracking link"}
                            >
                              <Truck className="h-4 w-4" />
                              Track Order
                            </button>
                          )}
                          
                          {/* Delete Order Button (Admin/Owner only) */}
                          <button
                            onClick={() => deleteOrder(order.id, order.order_number)}
                            disabled={actionLoading === order.id}
                            className="px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
                            title="Delete order permanently"
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete Order
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination Controls */}
        {!loading && filteredOrders.length > 0 && (
          <div className="mt-6 flex items-center justify-between bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="text-sm text-gray-600">
              Showing {indexOfFirstOrder + 1} to {Math.min(indexOfLastOrder, filteredOrders.length)} of {filteredOrders.length} orders
            </div>
            
            <div className="flex items-center gap-2">
              {/* Previous Button */}
              <button
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed text-white"
                style={{ 
                  backgroundColor: currentPage === 1 ? '#94a3b8' : 'var(--theme-primary)',
                  borderColor: 'var(--theme-primary)'
                }}
                onMouseEnter={(e) => {
                  if (currentPage !== 1) e.target.style.backgroundColor = 'rgb(33, 150, 199)';
                }}
                onMouseLeave={(e) => {
                  if (currentPage !== 1) e.target.style.backgroundColor = 'var(--theme-primary)';
                }}
              >
                Previous
              </button>

              {/* Page Numbers */}
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNumber => {
                  // Show first page, last page, current page, and pages around current
                  const showPage = 
                    pageNumber === 1 || 
                    pageNumber === totalPages || 
                    (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1);
                  
                  // Show ellipsis
                  const showEllipsisBefore = pageNumber === currentPage - 2 && currentPage > 3;
                  const showEllipsisAfter = pageNumber === currentPage + 2 && currentPage < totalPages - 2;

                  if (showEllipsisBefore || showEllipsisAfter) {
                    return <span key={pageNumber} className="px-2 text-gray-400">...</span>;
                  }

                  if (!showPage) return null;

                  const isActive = currentPage === pageNumber;

                  return (
                    <button
                      key={pageNumber}
                      onClick={() => goToPage(pageNumber)}
                      className={`px-3 py-1.5 text-sm rounded-lg ${
                        isActive
                          ? 'text-white'
                          : 'border text-white'
                      }`}
                      style={{
                        backgroundColor: isActive ? 'var(--theme-primary)' : 'transparent',
                        borderColor: 'var(--theme-primary)',
                        color: isActive ? 'white' : 'var(--theme-primary)'
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) {
                          e.target.style.backgroundColor = 'rgba(41, 173, 211, 0.1)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) {
                          e.target.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      {pageNumber}
                    </button>
                  );
                })}
              </div>

              {/* Next Button */}
              <button
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed text-white"
                style={{ 
                  backgroundColor: currentPage === totalPages ? '#94a3b8' : 'var(--theme-primary)',
                  borderColor: 'var(--theme-primary)'
                }}
                onMouseEnter={(e) => {
                  if (currentPage !== totalPages) e.target.style.backgroundColor = 'rgb(33, 150, 199)';
                }}
                onMouseLeave={(e) => {
                  if (currentPage !== totalPages) e.target.style.backgroundColor = 'var(--theme-primary)';
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Share Modal */}
      {shareModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-5 border-b border-gray-200 bg-[rgb(41,173,211)]">
              <h3 className="text-lg font-semibold text-white">Share Order</h3>
            </div>
            
            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-600">
                Share this link with the customer to allow them to view their order details:
              </p>
              
              <div className="flex gap-2">
                <input
                  type="text"
                  value={shareLink}
                  readOnly
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                />
                <button
                  onClick={copyToClipboard}
                  className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 ${
                    linkCopied 
                      ? 'bg-green-600 text-white' 
                      : 'bg-theme-blue text-white hover:bg-blue-700'
                  }`}
                >
                  {linkCopied ? (
                    <>
                      <Check className="h-4 w-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" />
                      Copy
                    </>
                  )}
                </button>
              </div>
            </div>
            
            <div className="flex gap-3 p-5 border-t border-gray-200">
              <button
                onClick={() => setShareModalOpen(false)}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {errorModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-5 border-b border-gray-200 bg-red-500">
              <h3 className="text-lg font-semibold text-white">Error</h3>
            </div>

            <div className="p-5">
              <div className="flex items-start gap-3">
                <XCircle className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-700">{errorMessage}</p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 p-5 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setErrorModalOpen(false);
                  setErrorMessage('');
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Track Order Modal */}
      {trackOrderModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-5 border-b border-gray-200 bg-[rgb(41,173,211)]">
              <h3 className="text-lg font-semibold text-white">
                {selectedOrderForTracking?.tracking_link ? 'Edit Tracking Link' : 'Add Tracking Link'}
              </h3>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Order Number
                </label>
                <input
                  type="text"
                  value={selectedOrderForTracking?.order_number || ''}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Tracking Link (USPS/UPS) *
                </label>
                <input
                  type="url"
                  placeholder="e.g., https://tools.usps.com/go/TrackConfirmAction?tLabels=..."
                  value={trackingLink}
                  onChange={(e) => setTrackingLink(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter the full tracking URL from USPS, UPS, FedEx, or other shipping carriers
                </p>
              </div>
            </div>

            <div className="flex gap-3 p-5 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setTrackOrderModalOpen(false);
                  setSelectedOrderForTracking(null);
                  setTrackingLink('');
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!trackingLink.trim() || actionLoading === selectedOrderForTracking?.id}
                onClick={saveTrackingLink}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-[rgb(41,173,211)] rounded-lg hover:bg-[rgb(31,153,191)] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading === selectedOrderForTracking?.id ? 'Saving...' : 'Save Tracking Link'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageOrders;
